import React, { useEffect, useMemo, useState } from "react";
import {
  PROVIDER_VERIFICATION_DOCUMENT_TYPES,
  uploadProviderVerificationDocument,
} from "../APIs/providerApi";

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const DOCUMENT_TYPE_LABELS = {
  GOVERNMENT_ID: "Government ID",
  MEDICAL_LICENSE: "Medical License",
  DEA_LICENSE: "DEA License",
  BOARD_CERTIFICATION: "Board Certification",
  NURSING_LICENSE: "Nursing License",
  PHYSICIAN_ASSISTANT_LICENSE: "Physician Assistant License",
  THERAPIST_LICENSE: "Therapist License",
  EMPLOYMENT_VERIFICATION: "Employment Verification",
  FACILITY_BADGE: "Facility Badge",
  BUSINESS_CARD: "Business Card",
  MALPRACTICE_INSURANCE: "Malpractice Insurance",
  OTHER: "Other",
};

function ProviderVerificationDocumentModal({
  open,
  npi,
  onBack,
  onClose,
  onUploaded,
}) {
  const [documentType, setDocumentType] = useState("MEDICAL_LICENSE");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDocumentType("MEDICAL_LICENSE");
    setFile(null);
    setLoading(false);
    setError(null);
  }, [open]);

  const canUpload = useMemo(
    () => Boolean(documentType && file && !loading),
    [documentType, file, loading],
  );

  if (!open) return null;

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0] || null;
    setError(null);

    if (!selected) {
      setFile(null);
      return;
    }

    if (!ALLOWED_MIME_TYPES.includes(selected.type)) {
      setFile(null);
      setError("Upload a PDF, JPEG, or PNG document.");
      return;
    }

    if (selected.size > MAX_FILE_BYTES) {
      setFile(null);
      setError("Document must be 5 MB or smaller.");
      return;
    }

    setFile(selected);
  };

  const handleUpload = async () => {
    if (!canUpload) return;

    try {
      setLoading(true);
      setError(null);
      await uploadProviderVerificationDocument(npi, documentType, file);
      onUploaded?.();
    } catch (err) {
      console.error("Provider verification document upload failed:", err);
      setError(
        err?.response?.data?.message ||
          "Unable to upload verification document. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          x
        </button>

        <h2 className="text-xl font-bold mb-2 text-center">Verify Identity</h2>
        <p className="text-sm text-gray-600 mb-5 text-center">
          To protect provider accounts, please upload one document for review.
        </p>

        <div className="space-y-4">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">
              Document type
            </span>
            <select
              value={documentType}
              onChange={(event) => setDocumentType(event.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            >
              {PROVIDER_VERIFICATION_DOCUMENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {DOCUMENT_TYPE_LABELS[type] || type}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">
              Upload document
            </span>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={handleFileChange}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
          </label>

          <p className="text-xs text-gray-500">PDF, JPEG, or PNG. Max 5 MB.</p>
        </div>

        {error && <div className="mt-4 text-sm text-red-600">{error}</div>}

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onBack || onClose}
            className="px-3 py-2 rounded border text-gray-700"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={!canUpload}
            className={`px-4 py-2 rounded ${
              canUpload
                ? "bg-blue-500 text-white hover:bg-blue-600"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          >
            {loading ? "Uploading..." : "Upload and Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProviderVerificationDocumentModal;
